import { useCallback, useState } from 'react';
import { faLink } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useCurrentEditor, useEditorState } from '@tiptap/react';

import { ButtonEditorSettingToggle } from '@/components/shared/button-editor';
import {
  LinkSettingsFields,
  type LinkSettingsValue,
} from '@/components/shared/link-settings-fields';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Toggle } from '@/components/ui/toggle';
import {
  getButtonHref,
  getButtonRel,
  getButtonTarget,
  isPageLinkType,
  normalizeButtonConfig,
} from '@/lib/blocks/button-links';
import { getPageDisplayPath, getPageDisplayTitle } from '@/lib/page-utils';
import { usePublishedPagesState } from '@/stores/pages-store';
import type { Page } from '@/types/page';

const BUTTON_LINK_CLASS = 'button-link';

interface LinkSelectorProps {
  onOpenChange?: (open: boolean) => void;
}

function findPageByHref(pages: Page[], href: string): Page | undefined {
  return pages.find((page) => getPageDisplayPath(page) === href);
}

export function LinkSelector({ onOpenChange }: LinkSelectorProps = {}) {
  const { editor } = useCurrentEditor();
  const [open, setOpen] = useState(false);
  const [linkSettings, setLinkSettings] = useState<LinkSettingsValue>({
    linkType: 'external',
    link: '',
  });
  const [styleAsButton, setStyleAsButton] = useState(false);
  const [selectedPage, setSelectedPage] = useState<Page | undefined>(undefined);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isLink: ctx.editor?.isActive('link') ?? false,
    }),
  });
  const { publishedPages } = usePublishedPagesState();

  const handlePageSelect = useCallback((page: Page | undefined) => {
    setSelectedPage((current) => {
      if (!page) return undefined;
      if (
        current?.id === page.id &&
        getPageDisplayPath(current) === getPageDisplayPath(page) &&
        getPageDisplayTitle(current) === getPageDisplayTitle(page)
      ) {
        return current;
      }
      return page;
    });
  }, []);

  if (!editor || !editorState) {
    return null;
  }

  const isLinkActive = editorState.isLink;
  const canSubmit = isPageLinkType(linkSettings.linkType)
    ? selectedPage?.id === linkSettings.internalPageId
    : Boolean(linkSettings.link?.trim());

  const updateOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const handleSettingsChange = (nextValue: LinkSettingsValue) => {
    if (
      !isPageLinkType(nextValue.linkType) ||
      nextValue.internalPageId !== linkSettings.internalPageId
    ) {
      setSelectedPage(undefined);
    }
    setLinkSettings(nextValue);
  };

  const handleSetLink = () => {
    if (!canSubmit) return;

    const resolvedPage = isPageLinkType(linkSettings.linkType) ? selectedPage : undefined;
    const destination = {
      ...linkSettings,
      link: resolvedPage ? getPageDisplayPath(resolvedPage) : linkSettings.link,
    };
    const href = getButtonHref(destination);
    if (!href) return;

    const attrs = {
      href,
      target: getButtonTarget(destination) ?? null,
      rel: getButtonRel(destination) ?? null,
      class: styleAsButton ? BUTTON_LINK_CLASS : null,
      internalPageId: isPageLinkType(destination.linkType)
        ? destination.internalPageId || null
        : null,
    };
    const { empty } = editor.state.selection;

    if (empty && !editor.isActive('link')) {
      const linkText = resolvedPage
        ? getPageDisplayTitle(resolvedPage)
        : destination.link?.trim() || href;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: linkText,
          marks: [{ type: 'link', attrs }],
        })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink(attrs).run();
    }
    updateOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    updateOpen(nextOpen);
    if (!nextOpen) return;

    const attrs = editor.getAttributes('link');
    const href = typeof attrs.href === 'string' ? attrs.href : '';
    const internalPageId =
      typeof attrs.internalPageId === 'string' && attrs.internalPageId !== ''
        ? attrs.internalPageId
        : undefined;
    const matchedPage = internalPageId ? undefined : findPageByHref(publishedPages, href);
    const resolvedPage = internalPageId
      ? publishedPages.find((page) => page.id === internalPageId)
      : matchedPage;
    const normalized = normalizeButtonConfig({
      text: '',
      link: href,
      linkType: internalPageId || matchedPage ? 'page' : undefined,
      internalPageId: internalPageId || matchedPage?.id,
      openInNewTab: attrs.target === '_blank',
    });

    setSelectedPage(resolvedPage);
    setLinkSettings({
      linkType: normalized.linkType || 'external',
      link: normalized.link,
      internalPageId: normalized.internalPageId,
      openInNewTab:
        (normalized.linkType === 'external' || normalized.linkType === 'page') &&
        normalized.openInNewTab,
      nofollow: false,
    });
    setStyleAsButton(
      typeof attrs.class === 'string' && attrs.class.split(' ').includes(BUTTON_LINK_CLASS),
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Toggle
          pressed={isLinkActive}
          data-state={isLinkActive ? 'on' : 'off'}
          aria-label="Toggle link"
        >
          <Icon icon={faLink} />
        </Toggle>
      </PopoverTrigger>
      <PopoverContent
        data-rich-text-editor-ui="format-toolbar-submenu"
        sideOffset={5}
        align="start"
        className="w-96 p-3"
      >
        <div className="flex flex-col gap-4">
          <LinkSettingsFields
            value={linkSettings}
            onChange={handleSettingsChange}
            enableSearch={open}
            onPageSelect={handlePageSelect}
            allowPageOpenInNewTab
            showNofollow={false}
          />
          <ButtonEditorSettingToggle
            title="Style as button"
            checked={styleAsButton}
            onCheckedChange={setStyleAsButton}
          />
          <div className="flex items-center justify-between gap-4 pt-2">
            {isLinkActive ? (
              <Button
                variant="outline"
                colorScheme="secondary"
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  updateOpen(false);
                }}
              >
                Remove Link
              </Button>
            ) : null}
            <div className="ml-auto flex gap-4">
              <Button
                variant="outline"
                colorScheme="secondary"
                onClick={() => {
                  editor.commands.focus();
                  updateOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSetLink} disabled={!canSubmit}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
